/**
 * Global error handling utilities
 */

import { toast } from 'sonner';
import { cloudflareErrorMonitor } from './cloudflare-error-monitor';

// Error severity levels
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

// Error categories
export enum ErrorCategory {
  NETWORK = 'network',
  AUTH = 'auth',
  VALIDATION = 'validation',
  SYSTEM = 'system',
  USER_INPUT = 'user_input',
  STORAGE = 'storage',
  SYNC = 'sync',
}

export interface ErrorContext {
  severity?: ErrorSeverity;
  category?: ErrorCategory;
  userId?: string;
  sessionId?: string;
  metadata?: Record<string, unknown>;
  retry?: () => Promise<void>;
}

class ErrorHandler {
  private errorQueue: Array<{
    error: Error;
    context?: ErrorContext;
    timestamp: Date;
  }> = [];
  private maxQueueSize = 100;

  /**
   * Initialize global error handlers
   */
  initialize() {
    // Initialize Cloudflare error monitoring
    cloudflareErrorMonitor.initialize();

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(
        new Error(`Unhandled Promise Rejection: ${event.reason}`),
        {
          severity: ErrorSeverity.HIGH,
          category: ErrorCategory.SYSTEM,
          metadata: { reason: event.reason },
        }
      );

      // Prevent default console error
      event.preventDefault();
    });

    // Handle global errors
    window.addEventListener('error', (event) => {
      this.handleError(new Error(event.message || 'Unknown error'), {
        severity: ErrorSeverity.HIGH,
        category: ErrorCategory.SYSTEM,
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });

      // Prevent default console error for handled errors
      event.preventDefault();
    });
  }

  /**
   * Handle an error with context
   */
  handleError(error: Error, context?: ErrorContext) {
    // Add to queue
    this.addToQueue(error, context);

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[ErrorHandler]', error, context);
    }

    // Send to error tracking service in production
    if (import.meta.env.PROD) {
      this.sendToErrorTracking(error, context);
      // Also report to Cloudflare for anonymous analytics
      cloudflareErrorMonitor.reportError(error, context);
    }

    // Show user notification based on severity
    this.notifyUser(error, context);
  }

  /**
   * Add error to queue for batch processing
   */
  private addToQueue(error: Error, context?: ErrorContext) {
    this.errorQueue.push({
      error,
      context,
      timestamp: new Date(),
    });

    // Limit queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }
  }

  /**
   * Send error to local storage for privacy-compliant monitoring
   */
  private sendToErrorTracking(error: Error, context?: ErrorContext) {
    // Store errors locally for privacy-compliant monitoring
    // No external tracking services are used
    try {
      const errors = JSON.parse(localStorage.getItem('error_log') || '[]');
      errors.push({
        message: error.message,
        stack: error.stack,
        context,
        timestamp: new Date().toISOString(),
      });

      // Keep only last 50 errors
      const recentErrors = errors.slice(-50);
      localStorage.setItem('error_log', JSON.stringify(recentErrors));
    } catch (e) {
      // Silently fail if storage is full
    }
  }

  /**
   * Show user-friendly error notification
   */
  private notifyUser(error: Error, context?: ErrorContext) {
    const severity = context?.severity || ErrorSeverity.MEDIUM;
    const category = context?.category || ErrorCategory.SYSTEM;

    // Determine message based on category
    let userMessage = 'An unexpected error occurred';
    // let _showRetry = false;

    switch (category) {
      case ErrorCategory.NETWORK:
        userMessage = 'Network error. Please check your connection.';
        // _showRetry = true;
        break;
      case ErrorCategory.AUTH:
        userMessage = 'Authentication error. Please sign in again.';
        break;
      case ErrorCategory.VALIDATION:
        userMessage = 'Invalid input. Please check your data.';
        break;
      case ErrorCategory.STORAGE:
        userMessage = 'Storage error. Your changes may not be saved.';
        // _showRetry = true;
        break;
      case ErrorCategory.SYNC:
        userMessage = 'Sync error. Changes will retry automatically.';
        break;
      case ErrorCategory.USER_INPUT:
        userMessage = error.message; // Use the actual error for user input issues
        break;
    }

    // Show toast notification
    if (severity === ErrorSeverity.CRITICAL) {
      toast.error(userMessage, {
        description: import.meta.env.DEV ? error.message : undefined,
        action: context?.retry
          ? {
              label: 'Retry',
              onClick: () => context.retry?.(),
            }
          : undefined,
      });
    } else if (severity === ErrorSeverity.HIGH) {
      toast.error(userMessage);
    } else if (severity === ErrorSeverity.MEDIUM) {
      toast.warning(userMessage);
    } else {
      // Low severity - only show in dev
      if (import.meta.env.DEV) {
        toast.info(userMessage);
      }
    }
  }

  /**
   * Get error history for debugging
   */
  getErrorHistory() {
    return this.errorQueue;
  }

  /**
   * Clear error history
   */
  clearErrorHistory() {
    this.errorQueue = [];
  }

  /**
   * Report errors to backend
   */
  async reportErrors() {
    try {
      const errors = JSON.parse(localStorage.getItem('error_log') || '[]');
      if (errors.length === 0) return;

      // TODO: Send to backend API
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   body: JSON.stringify(errors),
      // });

      // Clear after successful send
      localStorage.removeItem('error_log');
    } catch (e) {
      // Silently fail
    }
  }
}

// Singleton instance
export const errorHandler = new ErrorHandler();

/**
 * Retry utility with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffFactor?: number;
    onRetry?: (attempt: number, error: Error) => void;
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    onRetry,
  } = options;

  let lastError: Error;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      if (i === maxRetries - 1) {
        throw lastError;
      }

      const delay = Math.min(
        initialDelay * Math.pow(backoffFactor, i),
        maxDelay
      );
      onRetry?.(i + 1, lastError);

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

/**
 * Safe async wrapper for error handling
 */
export function safeAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context?: ErrorContext
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      errorHandler.handleError(error as Error, context);
      throw error;
    }
  }) as T;
}

/**
 * Create a retry function with context
 */
export function createRetryFunction<T>(
  fn: () => Promise<T>,
  context?: ErrorContext
): () => Promise<T> {
  return () =>
    retryWithBackoff(fn, {
      onRetry: (attempt, error) => {
        errorHandler.handleError(error, {
          ...context,
          severity: ErrorSeverity.LOW,
          metadata: {
            ...context?.metadata,
            retryAttempt: attempt,
          },
        });
      },
    });
}
